import { array, boolean, dateTime, defineAction, enumerated, number, object, string } from '~/prix'
import { spaceForPoint } from '~/prix/types/presets/space'
import operationalMonitoringSnapshotAction from './operationalMonitoringSnapshot.action.json' assert { type: 'json' }

const requestState = enumerated({
  searchingAgents: 'searching_agents',
  agentAccepted: 'agent_accepted',
  agentArrived: 'agent_arrived',
  inAttendance: 'in_attendance',
  allAgentsRefused: 'all_agents_refused',
  noAgentsAvailable: 'no_agents_available',
  done: 'done',
  canceled: 'canceled',
  unknown: 'unknown',
})

const operationalMonitoringSnapshotDefinition = defineAction({
  ...operationalMonitoringSnapshotAction,
  userActivityLogInput: 'omit' as const,
  operation: 'read' as const,
  input: object({
    selectedDate: string(),
    space: spaceForPoint().isOptional(),
    detailed: boolean().isOptional(),
  }),
  output: object({
    snapshotId: string(),
    generatedAt: dateTime(),
    selectedDate: string(),
    mode: enumerated({ historical: 'historical', live: 'live' }),
    businessTimeZone: string(),
    freshnessThresholdSeconds: number(),
    truncated: object({
      agents: boolean(),
      requests: boolean(),
    }),
    agents: array(
      object({
        id: string(),
        name: string().isOptional(),
        anonymizedCpf: string(),
        location: object({
          type: enumerated({ point: 'Point' }),
          coordinates: array(number()),
          accuracyMeters: number().isOptional(),
          capturedAt: dateTime(),
          updatedAt: dateTime(),
          freshness: enumerated({
            fresh: 'fresh',
            stale: 'stale',
          }),
        }),
        isAvailable: boolean(),
        activity: object({
          state: enumerated({
            activeToday: 'active_today',
            acceptedRequest: 'accepted_request',
          }),
          requestKey: string().isOptional(),
          drivingDurationSeconds: number().isOptional(),
          stoppedAt: dateTime().isOptional(),
        }),
        isInsideSelectedArea: boolean(),
      }),
    ),
    requests: array(
      object({
        id: string(),
        state: requestState,
        companyName: string().isOptional(),
        legalEntityCnpj: string().isOptional(),
        addressDisplay: string().isOptional(),
        companySize: string().isOptional(),
        segmentDescription: string().isOptional(),
        maturity: string().isOptional(),
        corporateName: string().isOptional(),
        tradeName: string().isOptional(),
        openingDate: dateTime().isOptional(),
        registrationStatus: string().isOptional(),
        location: object({
          type: enumerated({ point: 'Point' }),
          coordinates: array(number()),
          accuracyMeters: number().isOptional(),
        }),
        lastRecordedAt: dateTime(),
        closedAt: dateTime().isOptional(),
        attendanceStartedAt: dateTime().isOptional(),
        acceptedAgentId: string().isOptional(),
        routing: object({
          status: enumerated({
            calculated: 'calculated',
            unavailable: 'unavailable',
          }).isOptional(),
          distanceMeters: number().isOptional(),
          durationSeconds: number().isOptional(),
          geometry: object({
            type: enumerated({
              lineString: 'LineString',
            }),
            coordinates: array(array(number())),
          }).isOptional(),
          calculatedAt: dateTime().isOptional(),
          estimatedArrivalAt: dateTime().isOptional(),
        }),
        isInsideSelectedArea: boolean(),
        isPreviousDayPending: boolean(),
        anomalies: array(
          enumerated({
            multipleAcceptedResponses: 'multiple_accepted_responses',
            acceptedStateWithoutAgent: 'accepted_state_without_agent',
            unknownState: 'unknown_state',
          }),
        ),
      }),
    ),
    breakdowns: object({
      sizes: array(object({ type: string(), count: number() })),
      segments: array(object({ type: string(), count: number() })),
      maturities: array(object({ type: string(), count: number() })),
    }),
    dataCoverage: object({
      agentActivityAvailableFrom: string().isOptional(),
      agentActivityStatus: enumerated({
        available: 'available',
        unavailable: 'unavailable',
      }),
    }),
    dataQuality: object({
      requestsWithMultipleAcceptedResponses: number(),
      acceptedRequestsWithoutAcceptedAgent: number(),
    }),
  }),
})

export default operationalMonitoringSnapshotDefinition
